// Developer API → backend /developer router (the read-only developer surface) plus
// the dev-verdict writes, which live with the batch they belong to (/exe, /extension).
import { api, qs, filenameFromDisposition, getApiBase, getToken } from "./client";

// Projects a tester assigned to this developer.
export const listDevProjects = () => api.get("/developer/projects");
export const getDevProject = (id) => api.get(`/developer/projects/${id}`);

// One batch's report table: every case with the agent's result AND the dev verdict.
// `kind` is "exe" | "extension".
// Filters and paging go to the server, so the table pages against the true match
// count rather than whatever fitted in the first response.
export const getDevBatchReport = (kind, batchId, params = {}) =>
  api.get(`/developer/batches/${kind}/${batchId}${qs(params)}`);

// Download one run's tested-report sheet (XLSX).
//
// The server rebuilds the workbook on every request and sends it `no-store`, so
// what lands in Downloads is always the CURRENT state of the run — including
// verdicts recorded moments earlier and any re-run the tester has made since.
// The cache-buster is belt-and-braces against an intermediate proxy: a stale
// report is the one artefact that gets forwarded to other people.
//
// A plain <a href> can't be used here — the endpoint needs the Bearer token —
// hence fetch → blob → synthetic click.
export async function downloadDevBatchReport(kind, batchId) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(
    `${getApiBase()}/developer/batches/${kind}/${batchId}/report`,
    origin,
  );
  url.searchParams.set("t", String(Date.now()));
  const token = getToken();
  const res = await fetch(url.toString(), {
    cache: "no-store",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // The body is JSON on an error and XLSX on success, so read it only here.
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.detail || body?.error?.message || "";
    } catch (e) { /* non-JSON error body — fall back to the status */ }
    throw new Error(detail || `Report download failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const filename = filenameFromDisposition(cd, `report_${batchId}.xlsx`);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
  return { filename, sizeBytes: blob.size };
}

// Record the developer's verdict on one case. Passing dev_status: null clears it.
// This never overwrites the agent's result — it flags the case for verification on
// the tester's next re-run.
export const setDevStatus = (kind, batchId, itemId, { devStatus, note } = {}) =>
  api.patch(`/${kind}/batches/${batchId}/items/${itemId}/dev-status`, {
    dev_status: devStatus ?? null,
    note: note ?? null,
  });

// One verdict across many cases. Pass `itemIds` for an explicit selection, or
// `allMatching` with the table's current filters to cover every case the filter
// found — the report pages server-side, so "select all" would otherwise only
// reach the rows currently on screen.
export const setDevStatusBulk = (kind, batchId, {
  devStatus, note, itemIds = [], allMatching = false,
  status = null, devStatusFilter = null, search = null,
} = {}) =>
  api.post(`/${kind}/batches/${batchId}/dev-status/bulk`, {
    dev_status: devStatus ?? null,
    note: note ?? null,
    item_ids: itemIds,
    all_matching: allMatching,
    status,
    dev_status_filter: devStatusFilter,
    search,
  });

// Verdicts a developer may set (mirrors DEV_STATUSES on the backend).
export const DEV_STATUS_OPTIONS = [
  { value: "passed", label: "Passed", hint: "I fixed it — verify on the next re-run" },
  { value: "failed", label: "Failed", hint: "Still broken / not fixed yet" },
  { value: "blocked", label: "Blocked", hint: "Can't fix or verify yet" },
];
